import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ComponentInteractionService } from "src/app/services/component-interaction/component-interaction.service";
import { ShapeGeneratorService } from "src/app/services/shape-generator/shape-generator.service";
import { AnimationConfig, ShapeSelectorCanvasConfig } from "~/src/app/shared/models/shape-selector.model";
import { ColorCodes, ColorsPicker } from "@utils/color-constants";
import { MeshType } from "@utils/shape-type";
import * as THREE from "three";
import { Mesh } from "three";
import { InteractionService } from "../../shared/helpers/interaction.service";
import { take } from "rxjs";
import { ShapeResourceManagerService } from "../../shared/helpers/shape-resource-manager.service";

@Component({
    selector: "shape-selector",
    templateUrl: "./shape-selector.component.html",
    styleUrls: ["./shape-selector.component.scss"],
    standalone: false,
})
export class ShapeSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild("shapeSelector", { static: true }) shapeSelector!: ElementRef;

    private renderer = new THREE.WebGLRenderer({
        alpha: true,
        preserveDrawingBuffer: true,
        antialias: true,
    });

    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private shapes: THREE.Mesh[] = [];
    private raycaster = new THREE.Raycaster();
    private selectedShape: Mesh = new Mesh();
    private shapesToRotate: THREE.Mesh[] = [];
    private selectedColour: THREE.ColorRepresentation = ColorsPicker[0].value;
    private previousColor!: THREE.ColorRepresentation;
    private clock = new THREE.Clock();
    private hightlightMaterial = new THREE.MeshBasicMaterial(ShapeSelectorCanvasConfig.selectionHightlight);
    private highlightMesh: THREE.Mesh | null = null;

    constructor(
        private shapeGeneratorService: ShapeGeneratorService,
        private componentInteractionService: ComponentInteractionService,
        private interactionService: InteractionService,
        private shapeResourceManagerService: ShapeResourceManagerService,
    ) {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(
            ShapeSelectorCanvasConfig.cameraConfig.fov,
            ShapeSelectorCanvasConfig.RenderConfig.width / ShapeSelectorCanvasConfig.RenderConfig.height, // width by height gives proper angle for camera
        );
        this.renderer.setPixelRatio(window.devicePixelRatio);
    }

    ngOnDestroy(): void {}

    ngOnInit(): void {
        this.shapeResourceManagerService.initializeResources(true);
        this.componentInteractionService.getSelectedColor().subscribe((color) => {
            this.previousColor = this.selectedColour;
            this.interactionService.isSelected.pipe(take(1)).subscribe((res) => {
                if (!res) {
                    this.selectedColour = color;
                } else {
                    this.selectedColour = this.previousColor;
                }
                this.shapes.map((shape) => {
                    if (shape.material && shape.material instanceof THREE.MeshBasicMaterial) {
                        const color = new THREE.Color(this.selectedColour);
                        (shape.material as THREE.MeshBasicMaterial).color.copy(color);
                    }
                });
            });
        });
    }

    ngAfterViewInit(): void {
        this.canvasConfiguration();
    }

    @HostListener("click", ["$event"])
    onCanvasClick(event: MouseEvent): void {
        // Calculate normalized device coordinates and perform raycasting
        const canvasRect = this.renderer.domElement.getBoundingClientRect();
        const x = ((event.clientX - canvasRect.left) / canvasRect.width) * 2 - 1;
        const y = -((event.clientY - canvasRect.top) / canvasRect.height) * 2 + 1;

        // Update the raycaster with the mouse position
        this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);

        // Perform raycasting to check for intersections
        const intersects = this.raycaster.intersectObjects(this.shapes, false);

        let clickedShape;
        if (intersects.length > 0) {
            for (let i = 0; i < intersects.length; i++) {
                if (intersects[i].object.name !== "") {
                    clickedShape = intersects[i].object as Mesh;
                }
            }
            if (clickedShape) {
                this.setSelectedShape(clickedShape);
            }
        }
    }

    private canvasConfiguration(): void {
        this.renderer.setSize(
            ShapeSelectorCanvasConfig.RenderConfig.width,
            ShapeSelectorCanvasConfig.RenderConfig.height,
        );
        this.camera.position.z = ShapeSelectorCanvasConfig.RenderConfig.cameraPositionZ;
        this.shapeSelector.nativeElement.appendChild(this.renderer.domElement);
        const ambientLight = new THREE.AmbientLight(ColorCodes.white, 1); // 1 is the intensity of the light.
        this.scene.add(ambientLight);
        this.scene.add(this.camera);
        this.generateShapesForSelection();
        this.animate();
    }

    private animate(): void {
        requestAnimationFrame(() => this.animate());
        const delta: number = this.clock.getDelta();
        this.shapesToRotate.forEach((shape) => {
            shape.rotation.x += delta * AnimationConfig.rotation.x;
            shape.rotation.y += delta * AnimationConfig.rotation.y;
            shape.rotation.z += delta * AnimationConfig.rotation.z;
        });
        this.renderer.render(this.scene, this.camera);
    }

    private generateShapesForSelection(): void {
        const meshTypes = Object.values(MeshType) as MeshType[];

        let shapes: THREE.Mesh[] = [];

        meshTypes.forEach((type) => {
            const shape = this.shapeGeneratorService.generateShape(type, this.selectedColour, true);
            this.applyPositioning(shape, type, true);
            this.resetShapeRotation(shape);
            shapes.push(shape);
        });

        this.scene.add(...shapes);
        this.shapes.push(...shapes);
        this.shapesToRotate.push(...shapes);
        this.setShapesToRotate();
    }

    /**
     * Applies positioning offset for selector meshes
     */
    private applyPositioning(shape: THREE.Mesh, type: MeshType, isForSelector: boolean): void {
        if (!isForSelector) return;

        const offsetCoordinates = this.shapeResourceManagerService.getOffsetCoordinatesForSelector(type);
        if (offsetCoordinates) {
            shape.position.copy(offsetCoordinates);
        }
    }

    private resetShapeRotation(shape: Mesh): void {
        const lookAtPosition = shape.position.clone().add(ShapeSelectorCanvasConfig.lookAtOffset);
        shape.lookAt(lookAtPosition);
        shape.rotation.z = 0;
    }

    private setSelectedShape(shape: Mesh): void {
        this.selectedShape = shape;
        this.resetShapeRotation(this.selectedShape);
        this.setShapesToRotate();
        this.addHighlightMesh(shape);
        this.componentInteractionService.setSelectedShape(this.selectedShape.name as MeshType);
    }

    private addHighlightMesh(mesh: THREE.Mesh): void {
        if (this.highlightMesh) this.scene.remove(this.highlightMesh);
        const geometry = mesh.geometry.clone();
        this.highlightMesh = new THREE.Mesh(geometry, this.hightlightMaterial);

        this.highlightMesh.position.copy(mesh.position);
        this.highlightMesh.rotation.copy(mesh.rotation);
        this.highlightMesh.scale.copy(mesh.scale).addScalar(0.3);
        this.scene.add(this.highlightMesh);
    }

    public setShapesToRotate(): void {
        this.shapesToRotate = this.shapes.filter((shape) => shape.uuid !== this.selectedShape.uuid);
    }
}
